import { useState, useMemo } from 'react'
import { useChat } from '../contexts/ChatContext'
import { BlockGroup, SearchBar, ListRow, UnreadDot } from '@ofself/shell'
import { formatTime } from '../utils/time'

function formatShortTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000 && d.getDate() === now.getDate()) return formatTime(ts)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  if (diff < 7 * 86400000) return days[d.getDay()]
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function GroupsPage({ onSelectGroup }) {
  const { groups, groupChannelsByGroup, lastMessageMap, unreadCounts, activeChannel, selectChannel } = useChat()
  const [search, setSearch] = useState('')

  const filteredGroupSections = useMemo(() => {
    return groups.map(group => {
      let chs = (groupChannelsByGroup[group.id] || []).map(ch => ({ ...ch, _name: ch.name }))

      if (search.trim()) {
        const q = search.trim().toLowerCase()
        chs = chs.filter(c => c._name.toLowerCase().includes(q))
      }

      chs.sort((a, b) => {
        const tA = lastMessageMap[a.id]?.timestamp
        const tB = lastMessageMap[b.id]?.timestamp
        if (!tA && !tB) return 0
        if (!tA) return 1
        if (!tB) return -1
        return new Date(tB) - new Date(tA)
      })

      return { group, channels: chs }
    })
  }, [groups, groupChannelsByGroup, search, lastMessageMap])

  const sectionUnreadCounts = useMemo(() => {
    const counts = {}
    groups.forEach(g => {
      counts[g.id] = 0
      ;(groupChannelsByGroup[g.id] || []).forEach(ch => { if (unreadCounts[ch.id]) counts[g.id]++ })
    })
    return counts
  }, [groups, groupChannelsByGroup, unreadCounts])

  return (
    <div className="sidebar">
      <SearchBar
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        placeholder="Search conversations..."
      />

      <div className="block__body block-scroll" style={{ paddingTop: 16 }}>
        {filteredGroupSections.map(({ group, channels: groupChs }) => (
          <BlockGroup
            key={group.id}
            label={group.name}
            collapsible
            storageKey={`synapse_group_${group.id}`}
            unreadCount={sectionUnreadCounts[group.id] || 0}
          >
            <ul className="list-rows" style={{ padding: '0' }}>
              {groupChs.map(ch => {
                const isActive = activeChannel?.id === ch.id
                const hasUnread = !!unreadCounts[ch.id]
                const lastMsg = lastMessageMap[ch.id]
                const preview = lastMsg?.snippet || ''
                const timestamp = lastMsg?.timestamp
                const senderName = lastMsg?.senderName

                return (
                  <ListRow
                    key={ch.id}
                    avatar={
                      <div className="convo-avatar convo-avatar-channel">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="4" y1="9" x2="20" y2="9" />
                          <line x1="4" y1="15" x2="20" y2="15" />
                          <line x1="10" y1="3" x2="8" y2="21" />
                          <line x1="16" y1="3" x2="14" y2="21" />
                        </svg>
                      </div>
                    }
                    name={ch._name}
                    preview={preview ? (senderName ? `${senderName}: ${preview}` : preview) : undefined}
                    time={timestamp ? formatShortTime(timestamp) : undefined}
                    trailing={hasUnread ? <UnreadDot /> : undefined}
                    active={isActive}
                    unread={hasUnread}
                    onClick={() => onSelectGroup(ch)}
                  />
                )
              })}
            </ul>
          </BlockGroup>
        ))}
      </div>
    </div>
  )
}
